/** Durable store for bookmark image bytes, for realms with no service worker
 *  in front of the API.
 *
 *  markcat-app installs a CacheFirst route over these URLs (its vite.config.ts),
 *  so a reload there repaints tiles from disk. The extension's newtab page
 *  cannot have that: an MV3 background worker never receives fetch events from
 *  extension pages, and an extension page may not register a worker of its own.
 *  Without this every newtab re-downloads every visible tile. Same cache name as
 *  the app's route, so one purge covers whichever realm wrote the entry.
 *
 *  Entries carry no Vary, so a hit matches any request to that URL regardless of
 *  Authorization — clearAssetCache() must run on logout or the next account is
 *  served the previous one's tiles.
 */

export const IMAGE_CACHE_NAME = 'markcat-images-v1';

/** Mirrors the app's CacheFirst route so both realms age out alike. */
const MAX_ENTRIES = 600;
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
/** Written on store: a synthesized Response has no Date the browser would set. */
const CACHED_AT_HEADER = 'x-markcat-cached-at';

let opening: Promise<Cache> | null = null;

/** Null where CacheStorage is absent — jsdom tests, non-secure contexts. */
function openCache(): Promise<Cache> | null {
  if (typeof caches === 'undefined') return null;
  if (!opening) {
    opening = caches.open(IMAGE_CACHE_NAME);
    void opening.then(sweep).catch(() => {});
  }
  return opening;
}

function ageMs(res: Response): number {
  const stamp = Number(res.headers.get(CACHED_AT_HEADER));
  return Number.isFinite(stamp) ? Date.now() - stamp : Number.POSITIVE_INFINITY;
}

/** One pass per page load: drop what aged out, then trim oldest-first back to
 *  the cap. cache.keys() yields insertion order, so the excess is the head. */
async function sweep(cache: Cache): Promise<void> {
  const keys = await cache.keys();
  const excess = keys.length - MAX_ENTRIES;
  await Promise.all(keys.map(async (request, index) => {
    if (index < excess) {
      await cache.delete(request);
      return;
    }
    const stored = await cache.match(request);
    if (stored && ageMs(stored) > MAX_AGE_MS) await cache.delete(request);
  }));
}

export async function readCachedImage(url: string): Promise<Blob | null> {
  const cache = await openCache();
  if (!cache) return null;
  const stored = await cache.match(url);
  if (!stored) return null;
  if (ageMs(stored) > MAX_AGE_MS) {
    await cache.delete(url);
    return null;
  }
  return stored.blob();
}

export async function writeCachedImage(url: string, blob: Blob): Promise<void> {
  const cache = await openCache();
  if (!cache) return;
  await cache.put(url, new Response(blob, {
    headers: { 'Content-Type': blob.type, [CACHED_AT_HEADER]: String(Date.now()) },
  }));
}

/** Drop the whole store and the handle to it, so the next read reopens (and
 *  re-sweeps) rather than writing into a deleted cache. */
export async function deleteImageCache(): Promise<void> {
  opening = null;
  if (typeof caches !== 'undefined') await caches.delete(IMAGE_CACHE_NAME);
}
